import React, { useState } from 'react';
import {
    Box,
    Typography,
    Grid,
    Paper,
    Divider,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Chip,
    Card,
    CardContent,
    Link,
    Button
} from '@mui/material';

import {
    EmojiObjects,
    Kitchen,
    LocalLaundryService,
    Wifi,
    Group,
    ShoppingCart,
    CheckCircle,
    Star,
    AttachMoney,
    Home,
    PeopleAlt,
    LocalDining
} from '@mui/icons-material';

// スクロール用の関数
const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
        const offset = 80; // ヘッダーの高さ分のオフセット
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;

        window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
        });
    }
};

const tocItems = [
    { id: 'introduction', label: '導入・はじめに' },
    { id: 'ranking', label: 'ルームシェアで「本当に使える」家電ランキング' },
    { id: 'gadgets', label: 'あると便利！ルームシェア向けおすすめグッズ' },
    { id: 'tips', label: '共同購入のコツとトラブル防止ポイント' },
    { id: 'experiences', label: '実際のルームシェア体験談' },
    { id: 'summary', label: 'まとめ・あなたに合った家電選びのヒント' }
];

const PostContent = () => {
    const [showToc, setShowToc] = useState(true);
    return (
        <Box sx={{ maxWidth: 800, mx: 'auto', px: { xs: 1, sm: 3 } }}>
            {showToc ? (
                <Card sx={{ mb: 4, boxShadow: 2, bgcolor: '#e3f2fd', border: '1px solid #ddd', borderRadius: 2 }}>
                    <CardContent sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <Typography variant="h6" sx={{ flexGrow: 1 }}>目次</Typography>
                            <Button variant="outlined" size="small" onClick={() => setShowToc(false)}>閉じる</Button>
                        </Box>
                        <List sx={{ p: 0 }}>
                            {tocItems.map((item, idx) => (
                                <ListItem key={item.id} sx={{ p: 0.5, display: 'flex', alignItems: 'center' }}>
                                    <Box sx={{
                                        minWidth: 32, minHeight: 32, bgcolor: '#f5f5f5', color: '#757575',
                                        fontWeight: 'bold', borderRadius: 1, display: 'flex', alignItems: 'center',
                                        justifyContent: 'center', mr: 1, fontSize: '1.13rem'
                                    }}>
                                        {idx + 1}.
                                    </Box>
                                    <Link
                                        component="button"
                                        onClick={() => scrollToSection(item.id)}
                                        color="inherit"
                                        underline="hover"
                                        sx={{ cursor: 'pointer', fontSize: '0.97rem', textAlign: 'left', pl: 0 }}
                                    >
                                        {item.label}
                                    </Link>
                                </ListItem>
                            ))}
                        </List>
                    </CardContent>
                </Card>
            ) : (
                <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setShowToc(true)}
                    sx={{ mb: 2 }}
                >
                    目次を表示
                </Button>
            )}

            {/* 導入 */}
            <Box id="introduction" sx={{ mb: 4 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    ルームシェア生活を快適に！「本当におすすめの家電」ランキング
                </Typography>
                <Typography variant="body1" sx={{ mb: 1.5 }}>
                    ルームシェアは、家賃の節約や新しい出会い、楽しい共同生活が魅力。一方で、家電や便利グッズの選び方・使い方が生活の快適さを大きく左右します。<b>「どんな家電があれば毎日がもっとラクに、楽しくなるの？」</b> そんな疑問に応えるべく、人気ランキングから実際に役立つグッズ、共同購入のコツや体験談まで、徹底的にまとめました。ルームシェア初心者からベテランまで必見の内容です！
                </Typography>
                <Box sx={{ my: 2, textAlign: 'center' }}>
                    <img
                        src="/images/blog/living-dining-share_roomshare-osusume-kaden-ranking.jpg"
                        alt="明るい共有リビングで複数のルームメイトがソファに座り、笑顔で会話しながらコーヒーを楽しむ様子。温かみのあるウッド調インテリアと観葉植物が調和し、和やかで快適な空間を表現している。"
                        style={{ maxWidth: '100%', borderRadius: 8 }}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                        共有リビングは家電や便利グッズの活用で、さらに居心地の良い空間に
                    </Typography>
                </Box>
            </Box>

            {/* ランキング */}
            <Box id="ranking" sx={{ mb: 4 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    ルームシェアで「本当に使える」家電ランキング
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    共同生活のプロたちが選ぶ、<b>「これだけは絶対に外せない！」</b>という家電をランキング形式でご紹介。実際の満足度や役立ち度、トラブル回避にも注目しました。
                </Typography>

                <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12} sm={6}>
                        <Card sx={{ height: '100%', borderLeft: '6px solid #1976d2' }}>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                    <Star color="primary" sx={{ fontSize: 32, mr: 1 }} />
                                    <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 'bold' }}>第1位：大型冷蔵庫</Typography>
                                </Box>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    <Kitchen sx={{ verticalAlign: 'middle', color: '#1976d2', mb: '2px' }} /> ルームシェア最大の課題は「食材や飲み物の収納問題」。容量の大きい冷蔵庫なら、全員分のスペースをしっかり確保できます。<b>冷凍室が大きいと、作り置きや冷凍食品もシェアしやすく便利！</b>
                                </Typography>
                                <Chip label="共同購入おすすめ" color="primary" variant="outlined" size="small" sx={{ mr: 1 }} />
                                <Chip label="生活必需品" color="success" variant="outlined" size="small" />
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Card sx={{ height: '100%', borderLeft: '6px solid #0288d1' }}>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                    <Star color="primary" sx={{ opacity: 0.7, fontSize: 28, mr: 1 }} />
                                    <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 600 }}>第2位：洗濯乾燥機</Typography>
                                </Box>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    <LocalLaundryService sx={{ verticalAlign: 'middle', color: '#0288d1', mb: '2px' }} /> 共同生活では洗濯物がかさみがち。乾燥機能付きなら、天気に左右されず、時短＆カビ防止も。<b>シェア人数が多い場合は容量重視で選びましょう。</b>
                                </Typography>
                                <Chip label="時短" color="secondary" variant="outlined" size="small" sx={{ mr: 1 }} />
                                <Chip label="共同利用" color="primary" variant="outlined" size="small" />
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Card sx={{ height: '100%', borderLeft: '6px solid #388e3c' }}>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                    <Star color="primary" sx={{ opacity: 0.55, fontSize: 24, mr: 1 }} />
                                    <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 500 }}>第3位：Wi-Fiルーター（高速回線）</Typography>
                                </Box>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    <Wifi sx={{ verticalAlign: 'middle', color: '#388e3c', mb: '2px' }} /> テレワークや動画視聴、ゲームなど、<b>現代のルームシェアでネット環境は必須</b>。複数人同時接続でも安定する高速ルーターが人気です。中継機の導入もおすすめ。
                                </Typography>
                                <Chip label="ネット必須" color="success" variant="outlined" size="small" sx={{ mr: 1 }} />
                                <Chip label="ストレス軽減" color="secondary" variant="outlined" size="small" />
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Card sx={{ height: '100%', borderLeft: '6px solid #fbc02d' }}>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                    <Star color="disabled" sx={{ fontSize: 22, mr: 1 }} />
                                    <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 500 }}>第4位：電子レンジ・トースター</Typography>
                                </Box>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    <LocalDining sx={{ verticalAlign: 'middle', color: '#fbc02d', mb: '2px' }} /> 食事のタイミングがバラバラでも、サッと温められる電子レンジは大活躍。トースターも朝食やおやつに便利です。
                                </Typography>
                                <Chip label="時短調理" color="warning" variant="outlined" size="small" />
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Card sx={{ height: '100%', borderLeft: '6px solid #ab47bc' }}>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                    <Star color="disabled" sx={{ opacity: 0.5, fontSize: 22, mr: 1 }} />
                                    <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 500 }}>第5位：掃除機（コードレス推奨）</Typography>
                                </Box>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    <EmojiObjects sx={{ verticalAlign: 'middle', color: '#ab47bc', mb: '2px' }} /> 共有スペースの掃除がラクになるコードレス掃除機。<b>気になった時にサッと使えるので、キレイを保ちやすくなります。</b>
                                </Typography>
                                <Chip label="共有スペース" color="primary" variant="outlined" size="small" />
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    <CheckCircle sx={{ verticalAlign: 'middle', color: 'success.main', mr: 0.5 }} />
                    他にも「炊飯器」「電気ケトル」「ヘアドライヤー」など一定の人気がありますが、<b>全員で使うものは“容量・耐久性”重視</b>が鉄則です。
                </Typography>
            </Box>

            {/* 便利グッズ紹介 */}
            <Box id="gadgets" sx={{ mb: 4 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    あると便利！ルームシェア向けおすすめグッズ
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    家電のほかにも、ルームシェア生活を「もっと快適＆楽しく」する便利グッズをピックアップ。ちょっとした工夫で毎日がグッと変わります！
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <Paper elevation={3} sx={{ p: 2, borderRadius: 2, mb: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <ShoppingCart sx={{ color: '#1976d2', mr: 1 }} />
                                <Typography variant="subtitle1" fontWeight="bold">
                                    ラベル付き収納ケース
                                </Typography>
                            </Box>
                            <Typography variant="body2">
                                キッチンや冷蔵庫の中、洗面所の小物など、<b>誰のものか分かりやすく収納</b>できるグッズ。トラブル防止にも役立ちます。
                            </Typography>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Paper elevation={3} sx={{ p: 2, borderRadius: 2, mb: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <Group sx={{ color: '#0288d1', mr: 1 }} />
                                <Typography variant="subtitle1" fontWeight="bold">
                                    共有スケジュールボード
                                </Typography>
                            </Box>
                            <Typography variant="body2">
                                ゴミ出しや掃除の当番、イベント予定などを「見える化」。<b>コミュニケーション不足の解消</b>にも効果的です。
                            </Typography>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Paper elevation={3} sx={{ p: 2, borderRadius: 2, mb: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <AttachMoney sx={{ color: '#388e3c', mr: 1 }} />
                                <Typography variant="subtitle1" fontWeight="bold">
                                    家計簿アプリ・精算ツール
                                </Typography>
                            </Box>
                            <Typography variant="body2">
                                共同で購入したものの精算や割り勘が簡単に。<b>金銭トラブル防止</b>のためにも活用をおすすめします。
                            </Typography>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Paper elevation={3} sx={{ p: 2, borderRadius: 2, mb: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <Home sx={{ color: '#fbc02d', mr: 1 }} />
                                <Typography variant="subtitle1" fontWeight="bold">
                                    玄関のスマートキー・セキュリティカメラ
                                </Typography>
                            </Box>
                            <Typography variant="body2">
                                <b>セキュリティ強化</b>と入退室管理に。スマートキーはシェアメイトの入れ替わり時にも便利です。
                            </Typography>
                        </Paper>
                    </Grid>
                </Grid>
                <Box sx={{ my: 2, textAlign: 'center' }}>
                    <img
                        src="/images/blog/gadget-storage_roomshare-osusume-kaden-ranking.jpg"
                        alt="明るいキッチンカウンターに並ぶラベル付き収納ケースと、手書きのスケジュールボード。整理整頓された空間と、ルームメイト同士の円滑なコミュニケーションを象徴する雰囲気。"
                        style={{ maxWidth: '100%', borderRadius: 8 }}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                        収納ケースやスケジュールボードでストレスなしの共同生活を実現
                    </Typography>
                </Box>
            </Box>

            {/* 共同購入のコツ */}
            <Box id="tips" sx={{ mb: 4 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    共同購入のコツとトラブル防止ポイント
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    家電の共同購入は、コスト削減だけでなく「みんなが納得して使える」ことが大切。「後から揉めた…」を防ぐコツをチェックしましょう。
                </Typography>
                <Paper elevation={1} sx={{ p: 2, borderRadius: 2, mb: 2, bgcolor: '#f5f5f5' }}>
                    <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                        共同購入・共有家電のチェックポイント
                    </Typography>
                    <List dense>
                        <ListItem sx={{ py: 0.5 }}>
                            <ListItemIcon>
                                <CheckCircle color="primary" />
                            </ListItemIcon>
                            <ListItemText
                                primary="購入前に「用途」「予算」「誰が管理するか」を明確に話し合う"
                            />
                        </ListItem>
                        <ListItem sx={{ py: 0.5 }}>
                            <ListItemIcon>
                                <CheckCircle color="primary" />
                            </ListItemIcon>
                            <ListItemText
                                primary="購入後の「精算方法」「維持費」「処分時の取り決め」も決めておく"
                            />
                        </ListItem>
                        <ListItem sx={{ py: 0.5 }}>
                            <ListItemIcon>
                                <CheckCircle color="primary" />
                            </ListItemIcon>
                            <ListItemText
                                primary="高額家電や、故障しやすいものは保証期間や修理・買い替えルールも検討"
                            />
                        </ListItem>
                        <ListItem sx={{ py: 0.5 }}>
                            <ListItemIcon>
                                <CheckCircle color="primary" />
                            </ListItemIcon>
                            <ListItemText
                                primary="「誰がどの家電を持ち出せるか」「退去時の対応」も事前確認を"
                            />
                        </ListItem>
                    </List>
                </Paper>
                <Box sx={{ my: 2 }}>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                        <b>ルームメイト同士でLINEグループや家計簿アプリを活用し、購入や精算の履歴を残しておくと安心</b>です。小さなトラブルも事前のルール化で防ぐことができます。
                    </Typography>
                </Box>
                <Box sx={{ my: 2, textAlign: 'center' }}>
                    
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                        共同購入は事前のルール共有＆コミュニケーションが成功のカギ
                    </Typography>
                </Box>
            </Box>

            {/* 体験談 */}
            <Box id="experiences" sx={{ mb: 4 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    実際のルームシェア体験談
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    ルームシェア経験者に聞いた「買ってよかった家電」「ちょっと後悔したアイテム」「便利グッズの活用法」など、リアルな声をご紹介します。
                </Typography>
                <Paper elevation={1} sx={{ p: 2, borderRadius: 2, mb: 2 }}>
                    <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                        【体験談1】冷蔵庫のサイズで大正解！
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                        「最初は普通サイズの冷蔵庫を使っていましたが、すぐにパンパンに…。思い切って大型に買い替えたら、<b>全員分のスペースが明確に分けられてストレス激減！</b>作り置きや冷凍食品もシェアしやすくなりました」（30代・女性）
                    </Typography>
                </Paper>
                <Paper elevation={1} sx={{ p: 2, borderRadius: 2, mb: 2 }}>
                    <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                        【体験談2】Wi-Fiの速度は妥協しないで
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                        「ネットが遅くなるたびにイライラ…。<b>ルーターの買い替え＆中継機設置で一気に快適になりました。</b>オンライン会議や動画もストレスフリーです」（20代・男性）
                    </Typography>
                </Paper>
                <Paper elevation={1} sx={{ p: 2, borderRadius: 2, mb: 2 }}>
                    <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                        【体験談3】家計簿アプリで「誰が何を払った？」の混乱ゼロ
                    </Typography>
                    <Typography variant="body2">
                        「まとめ買いした洗剤や消耗品、毎月の光熱費など、<b>アプリで割り勘＆精算履歴を残す</b>ようにしたら、お金のことで揉めることがなくなりました！」（20代・女性）
                    </Typography>
                </Paper>
                <Box sx={{ my: 2, textAlign: 'center' }}>
                    <img
                        src="/images/blog/share-dining_roomshare-osusume-kaden-ranking.jpg"
                        alt="ダイニングテーブルを囲んで4人のルームメイトが夕食を楽しみながら、笑顔で会話しているシーン。暖色系の照明と賑やかな食卓の雰囲気が、家電や便利グッズが生み出す快適な共同生活を象徴している。"
                        style={{ maxWidth: '100%', borderRadius: 8 }}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                        共有家電や便利グッズが、日々のコミュニケーションや楽しい時間をサポート
                    </Typography>
                </Box>
            </Box>

            {/* まとめ */}
            <Box id="summary" sx={{ mb: 6 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    まとめ・あなたに合った家電選びのヒント
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    ルームシェア生活の快適さは、<b>家電やグッズ選び、そして「みんなが使いやすいルール作り」</b>で大きく変わります。人数や生活スタイル、予算に合わせて最適なアイテムを選び、無理のない範囲で「共同購入」や「シェア」を取り入れてみてください。
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    家電ひとつでストレスが減り、会話や笑顔が増える——そんな素敵なルームシェア生活のために、ぜひ今回のランキングやアドバイスを活用してみてください！
                </Typography>
            </Box>
        </Box>
    );
};

const post = {
    id: 'roomshare-osusume-kaden-ranking',
    slug: 'roomshare-osusume-kaden-ranking',
    title: 'ルームシェアの「おすすめ家電」ランキング',
    excerpt: '人気家電ランキング 便利グッズ紹介 共同購入のコツ 体験談',
    coverImage: '/images/blog/cover-roomshare-kaden_roomshare-osusume-kaden-ranking.jpg',
    coverImageAlt: '明るい自然光が差し込むモダンなルームシェアの共有リビングで、多様な背景を持つルームメイトたちが楽しく会話をしている様子。あたたかみのあるインテリアと最新家電が置かれ、快適で活気ある共同生活の雰囲気が伝わる。',
    publishedAt: '2024-06-23T09:00:00+09:00',
    isPublished: true,
    categories: ['ルームシェア', '家電', '賃貸生活'],
    tags: ['ルームシェア', '家電ランキング', '便利グッズ', '共同購入', '体験談'],
    author: {
        name: '山下 直樹',
        avatar: '/images/avatars/naoki-yamashita.jpg',
        bio: 'ルームシェアや賃貸生活、暮らしの工夫に詳しいライター。自身も複数回のシェア経験を持ち、現場目線でのアドバイスに定評がある。',
        profilePicture: '/images/avatars/naoki-yamashita.jpg'
    },
    viewCount: 1000,
    contentComponent: PostContent
};

export default post;